import { GrossBoardWeekMode, PayrollFrequency, PayrollPeriod, PayrollScheduleSegment, singleWeeklySchedule, toGrossBoardWeekMode } from '@numeo/types-core'

/**
 * Frontend bridge to the shared payroll-schedule resolver (mirrors the main-service adapter).
 *
 * Reads the company's payroll cadence timeline (`settings.company.payrollSchedule`) and falls
 * back to a synthesized single weekly segment from the legacy `payrollPeriod` anchor when no
 * timeline is present — so the admin computes the exact same period boundaries as the backend.
 */

interface CompanyLike {
    payrollPeriod?: PayrollPeriod
    payrollSchedule?: PayrollScheduleSegment[]
    grossBoardWeekMode?: unknown
}

interface ApplicationLike {
    settings?: { company?: CompanyLike }
}

/**
 * True once the application's company settings have loaded. Write paths (the page-load
 * statement sync) must gate on this: before settings arrive, getApplicationPayrollSchedule
 * silently falls back to a MONDAY_SUNDAY grid, and a range computed from that fallback must
 * never reach the backend (the phantom-period incident). Display-only period math may keep
 * using the fallback while loading.
 */
export const isPayrollScheduleLoaded = (application: ApplicationLike | null | undefined): boolean => !!application?.settings?.company

/** The company's schedule, or a synthesized single weekly segment for legacy/unmigrated companies. */
export const getApplicationPayrollSchedule = (application: ApplicationLike | null | undefined): PayrollScheduleSegment[] => {
    const company = application?.settings?.company
    if (company?.payrollSchedule && company.payrollSchedule.length > 0) {
        // effectiveStart may arrive as an ISO string over the wire — normalize to Date for the resolver.
        return company.payrollSchedule.map((segment) => ({ ...segment, effectiveStart: new Date(segment.effectiveStart) }))
    }
    return singleWeeklySchedule(company?.payrollPeriod ?? PayrollPeriod.MONDAY_SUNDAY)
}

/**
 * Splits the cadence timeline at `at`: the segment in force (the greatest `effectiveStart <= at`) and the
 * one scheduled to supersede it. Either can be null — `active` when every segment is future-dated, `next`
 * when nothing follows.
 *
 * The single walk over the timeline. Everything else in this file — the active cadence, the pending change,
 * the seam the board clips its week at — is a read off this one result, so they can never disagree about
 * which segment applies.
 */
const splitScheduleAt = (
    application: ApplicationLike | null | undefined,
    at: Date
): { active: PayrollScheduleSegment | null; next: PayrollScheduleSegment | null } => {
    const sorted = getApplicationPayrollSchedule(application).sort((a, b) => new Date(a.effectiveStart).getTime() - new Date(b.effectiveStart).getTime())
    let active: PayrollScheduleSegment | null = null
    let next: PayrollScheduleSegment | null = null
    for (const segment of sorted) {
        if (new Date(segment.effectiveStart).getTime() <= at.getTime()) active = segment
        else {
            next = segment
            break
        }
    }
    return { active, next }
}

/**
 * The cadence in effect **right now** — falling back to the earliest segment if all of them are
 * future-dated. Period math must use this; the settings selectors display the latest saved choice
 * instead (see {@link getApplicationDisplayFrequency} / {@link getApplicationDisplayAnchor}).
 */
const getApplicationActiveSegment = (application: ApplicationLike | null | undefined, at: Date): PayrollScheduleSegment | null => {
    const { active, next } = splitScheduleAt(application, at)
    // With nothing yet in force, `next` IS the earliest segment — the loop stops at the first future one.
    return active ?? next
}

export const getApplicationActiveFrequency = (application: ApplicationLike | null | undefined, at: Date = new Date()): PayrollFrequency => {
    return getApplicationActiveSegment(application, at)?.frequency ?? PayrollFrequency.WEEKLY
}

/** The anchor (period start day) of the cadence in effect **right now**. */
export const getApplicationActiveAnchor = (application: ApplicationLike | null | undefined, at: Date = new Date()): PayrollPeriod => {
    return getApplicationActiveSegment(application, at)?.anchor ?? PayrollPeriod.MONDAY_SUNDAY
}

/**
 * The cadence seam around `at`: the effective start of the segment in force, and the effective start of
 * the one that supersedes it (null when nothing follows).
 *
 * Payroll clips a period at a seam rather than letting it run its natural length — `resolvePeriod`'s
 * "transition stub" — so that no day ever belongs to two periods. Anything that draws a week from this
 * schedule has to clip the same way, or the changeover week overlaps its neighbour and the same load is
 * counted in both.
 */
export const getApplicationSegmentBounds = (
    application: ApplicationLike | null | undefined,
    at: Date = new Date()
): { from: Date | null; until: Date | null } => {
    const { active, next } = splitScheduleAt(application, at)
    return {
        from: active ? new Date(active.effectiveStart) : null,
        until: next ? new Date(next.effectiveStart) : null,
    }
}

export interface PendingFrequencyChange {
    frequency: PayrollFrequency
    /** Anchor (period start day) the pending change switches to — may differ from frequency-only changes. */
    anchor: PayrollPeriod
    effectiveStart: Date
}

/**
 * A scheduled-but-not-yet-effective cadence change (effectiveStart in the future), or null.
 * Carries both the pending `frequency` AND `anchor` so callers can surface a period-start-day
 * change even when the frequency itself is unchanged.
 */
export const getApplicationPendingFrequencyChange = (application: ApplicationLike | null | undefined, at: Date = new Date()): PendingFrequencyChange | null => {
    const pending = splitScheduleAt(application, at).next
    return pending ? { frequency: pending.frequency, anchor: pending.anchor, effectiveStart: new Date(pending.effectiveStart) } : null
}

/**
 * What the settings selectors should DISPLAY — the user's latest saved choice: the pending
 * (scheduled-but-not-yet-effective) cadence when one exists, else the active one. Without this a
 * just-saved change snaps back to the old value in the dropdown and reads as if it were ignored;
 * the PayrollScheduleBanner is what tells the user the change isn't live yet. Period math must
 * keep using the active helpers above.
 */
export const getApplicationDisplayFrequency = (application: ApplicationLike | null | undefined, at: Date = new Date()): PayrollFrequency => {
    return getApplicationPendingFrequencyChange(application, at)?.frequency ?? getApplicationActiveFrequency(application, at)
}

export const getApplicationDisplayAnchor = (application: ApplicationLike | null | undefined, at: Date = new Date()): PayrollPeriod => {
    return getApplicationPendingFrequencyChange(application, at)?.anchor ?? getApplicationActiveAnchor(application, at)
}

/**
 * True when the board applies the payroll period start day to EVERY week at once — immediately, and
 * backwards over history — instead of following payroll's forward-dated periods.
 *
 * False is the default and the state of every company that has never touched the setting, so the
 * board's existing behaviour is what "not configured" means: nothing changes for anyone who leaves
 * this alone. Any stale or hand-edited value falls back to that same default rather than silently
 * redrawing a company's whole history.
 */
export const isGrossBoardOnAllWeeks = (application: ApplicationLike | null | undefined): boolean => {
    return toGrossBoardWeekMode(application?.settings?.company?.grossBoardWeekMode) === GrossBoardWeekMode.ALL_WEEKS
}

/**
 * The anchor the board draws under ALL_WEEKS: the LAST segment on the timeline — the most recent period
 * start day the user has saved, whether or not payroll has switched to it yet.
 *
 * Not the ACTIVE anchor, and not the display one either. Active would leave the mode doing nothing at
 * all until a forward-dated change matured — the user picks a day, the board does not move, which is
 * the exact complaint the mode exists to answer. Display is `at`-dependent: on a timeline with more
 * than one future segment it returns the first one after `at`, so June's week and October's week would
 * be drawn on different days and the mode's one promise — every week on the same day — would break.
 * The last segment is the only reading that is both immediate and uniform.
 */
const getLatestScheduledAnchor = (application: ApplicationLike | null | undefined): PayrollPeriod => {
    const sorted = getApplicationPayrollSchedule(application).sort((a, b) => new Date(a.effectiveStart).getTime() - new Date(b.effectiveStart).getTime())
    return sorted[sorted.length - 1]?.anchor ?? PayrollPeriod.MONDAY_SUNDAY
}

/**
 * The weekday the board's week starts on.
 *
 * The single answer to "where does the board's week begin". The grid, the window sent to the server,
 * the live-update middleware and the "counts in week of X" label all resolve it here — a second copy
 * would let the grid draw one week while a label named another.
 *
 * Note the two branches differ in more than the value. The payroll branch is a function of `at` (the
 * week being viewed) because payroll changes are forward-dated and history keeps its old anchor; the
 * ALL_WEEKS branch ignores `at` entirely, which is exactly what makes one day apply to every week.
 */
export const getBoardWeekAnchor = (application: ApplicationLike | null | undefined, at: Date = new Date()): PayrollPeriod => {
    return isGrossBoardOnAllWeeks(application) ? getLatestScheduledAnchor(application) : getApplicationActiveAnchor(application, at)
}
